import { plural } from "../plural";

/** English strings for the app's workspace area. Keys are camelCase, grouped by screen. */
export default {
  previewTips: {
    tips: [
      {
        title: "Connect the tools you already use",
        body: "Gmail, Slack, Notion, Sheets and hundreds more — ask the agent to use them, no API keys in your code.",
      },
      {
        title: "Publish when it looks right",
        body: "One button puts your app on a public URL, and your own domain if you have one.",
      },
      {
        title: "A database appears when you need one",
        body: "Ask for something that has to be saved and Mythex sets it up — nothing to configure.",
      },
      {
        title: "Talk instead of typing",
        body: "The microphone in the composer dictates straight into your message.",
      },
      {
        title: "Plan before you build",
        body: "Switch the composer to Plan and the agent writes the approach out first, for you to change.",
      },
      {
        title: "Keep your code",
        body: "Sync to your own GitHub repository, or export the project and run it anywhere.",
      },
    ],
    tipOf: "Tip {n} of {total}",
    play: "Play tips",
    pause: "Pause tips",
  },
  terminal: {
    disconnected: "terminal disconnected",
  },
  fileViewer: {
    cantShow: "{name} can't be shown here.",
  },
  lightbox: {
    imagePreview: "Image preview",
    downloadImage: "Download image",
  },
  filePreview: {
    openInCode: "Open in Code",
    downloadFile: "Download file",
    couldntOpen: "Couldn't open {name}.",
    empty: "This file is empty.",
    openOnDevice: "Open it on your device to see it.",
  },
  platformTools: {
    publish: { label: "Publish", hint: "Put your app live" },
    mobile: { label: "Phone app", hint: "Android or iPhone" },
    secrets: { label: "Secrets", hint: "Add env vars for your app" },
    test: { label: "Test", hint: "Verify the app in the browser" },
    database: { label: "Database", hint: "Add Postgres for this app" },
    storage: { label: "Storage", hint: "Add file uploads for this app" },
    image: { label: "Create image", hint: "Generate a picture for your app" },
    fix: { label: "Fix", hint: "Repair what’s broken" },
  },
  platformToolsMenu: {
    noMatches: "No matching commands",
    commands: "Commands",
    removeCommand: "Remove command",
  },
  sharePreview: {
    title: "Share preview",
    intro: "Create a link so anyone can view your in-progress project.",
    unsupported: "Preview links need the hosted preview domain, so they don’t work in local development.",
    activeLinks: "Your active links",
    loadingLinks: "Loading links…",
    empty: "No links yet. Anyone you give one to can watch this project as you build it.",
    needsDomain: "Needs the hosted preview domain",
    notOpened: "Not opened yet",
    opened: plural({ one: "Opened once", other: "Opened {n} times" }),
    copiedToClipboard: "Link copied to clipboard",
    copyLinkFor: "Copy link for {name}",
    deleteLink: "Delete link",
    deleteLinkNamed: "Delete link {name}",
    createNew: "Create new preview link",
    backToLinks: "Back to your links",
    linkName: "Link name",
    linkNamePlaceholder: "e.g. Client review",
    linkNameHint: "Only you see this — it is here to tell your links apart.",
    createLink: "Create preview link",
    footnote:
      "Anyone with a link can view this project — they can’t edit it or see your code. Delete a link to cut off access.",
    loadFailed: "Couldn’t load your links.",
    limitReached: "That's every preview link this project can have. Delete one first.",
    nameTooLong: "Give the link a shorter name.",
    createFailed: "Couldn’t create the link.",
    copyFailed: "Couldn’t copy — select the link and copy it manually.",
    deleted: "“{name}” can’t open this preview any more.",
    deleteFailed: "Couldn’t delete that link. It still works — try again.",
  },
  previewPane: {
    needsCredits: "Preview needs credits",
    needsCreditsBody: "Starting a workspace uses credits. Add more, or wait for the daily grant.",
    needsCreditsBodyNoSale: "Starting a workspace uses credits. Your daily credits reset tomorrow.",
    addCredits: "Add credits",
    startFailed: "Couldn't start the workspace",
    startFailedBody: "The sandbox didn't come up. Trying again usually sorts it.",
    emptyTitle: "Your app will appear here",
    emptyBody: "Describe what you want in chat.",
    startingWorkspace: "Starting your workspace…",
    updatingApp: "Updating your app…",
    startingApp: "Starting your app…",
    almostThere: "Almost there — the first load can take a little longer.",
    gettingReady: "Getting your app ready…",
    appPreview: "App preview",
  },
  editorPalette: {
    commandPalette: "Command palette",
    goToFile: "Go to file",
    editorSettings: "Editor settings",
    keyboardShortcuts: "Keyboard shortcuts",
    searchFiles: "Search files (append : to go to line)",
    typeCommand: "Type a command…",
    noCommands: "No commands",
    noMatchingFiles: "No matching files",
    indexingFiles: "Indexing files…",
    recentlyOpened: "recently opened",
    wordWrap: "Word wrap",
    minimap: "Minimap",
    autoSave: "Auto save",
    fontSize: "Font size",
    tabSize: "Tab size",
    shortcuts: {
      saveFile: "Save file",
      closeTab: "Close tab",
      closeOtherTabs: "Close other tabs",
      reopenClosedTab: "Reopen closed tab",
      searchSidebar: "Search sidebar",
      toggleTerminal: "Toggle terminal",
      findInFile: "Find in file (Monaco)",
      replaceInFile: "Replace in file (Monaco)",
      goToLine: "Go to line (Monaco)",
      cheatsheet: "This cheatsheet",
    },
  },
  searchSidebar: {
    refresh: "Refresh",
    clearResults: "Clear Search Results",
    collapseAll: "Collapse All",
    hideFilters: "Hide files to include/exclude",
    showFilters: "Show files to include/exclude",
    matchCase: "Match Case",
    matchWholeWord: "Match Whole Word",
    useRegex: "Use Regular Expression",
    filesToInclude: "files to include",
    filesToExclude: "files to exclude",
    includePlaceholder: "e.g. *.ts, src",
    excludePlaceholder: "e.g. dist, *.min.js",
    typeToSearch: "Type to search across the project",
    searching: "Searching…",
    noResults: "No results found",
    summary: "{results} in {files}",
    results: plural({ one: "{n} result", other: "{n} results" }),
    files: plural({ one: "{n} file", other: "{n} files" }),
  },
  codeView: {
    explorer: "Explorer",
    newFile: "New file",
    newFolder: "New folder",
    refresh: "Refresh",
    sidebar: "Sidebar",
    searchWithKeys: "Search ({keys})",
    goToFileWithKeys: "Go to file ({keys})",
    noFilesOpen: "No files open",
    closeNamed: "Close {name}",
    markdownView: "Markdown view",
    preview: "Preview",
    code: "Code",
    editorSettings: "Editor settings",
    keyboardShortcuts: "Keyboard shortcuts",
    savingState: "saving…",
    savedState: "saved ✓",
    saveHint: "{keys} to save",
    readOnlyFree: "Code is read-only on Free. Claim 30% off Pro to edit files.",
    openFromExplorer: "Open a file from the explorer",
    emptyShortcuts: "{mod}+P go to file · {mod}+Shift+F search · {mod}+Shift+P commands",
    terminal: "Terminal",
    folderName: "folder name",
    fileName: "file name",
    deleteTitle: "Delete “{name}”?",
    deleteSubtitle: "This can’t be undone.",
    nameHasSlash: "Name can't include /",
    createFailed: "Couldn't create that.",
    renameFailed: "Couldn't rename that.",
    openFailed: "Couldn't open {path}",
    changedOnDisk: plural({
      one: "A file you edited changed on disk — your unsaved buffer was kept.",
      other: "{n} open files changed on disk — unsaved buffers were kept.",
    }),
    relativePathCopied: "Relative path copied",
    pathCopied: "Path copied",
    copyPathFailed: "Couldn't copy path",
    deleteFailed: "Couldn't delete that.",
    commands: {
      saveFile: "Save file",
      goToFile: "Go to file…",
      searchInFiles: "Search in files…",
      toggleTerminal: "Toggle terminal",
      newFile: "New file…",
      newFolder: "New folder…",
      reopenClosedTab: "Reopen closed tab",
      closeOtherTabs: "Close other tabs",
      closeSavedTabs: "Close saved tabs",
      closeAllTabs: "Close all tabs",
      editorSettings: "Editor settings…",
      refreshFiles: "Refresh files",
    },
    menu: {
      closeOthers: "Close Others",
      closeToRight: "Close to the Right",
      closeSaved: "Close Saved",
      closeAll: "Close All",
      copyPath: "Copy Path",
      copyRelativePath: "Copy Relative Path",
      revealInExplorer: "Reveal in Explorer",
      upgradeToEdit: "Upgrade to edit…",
    },
  },
  services: {
    website: "Website",
    app: "App",
    admin: "Admin",
    docs: "Docs",
    worker: "Worker",
    frontend: "Frontend",
    backend: "Backend",
    server: "Server",
    starting: "{name} · starting…",
    websites: "Websites",
    servers: "Servers",
  },
  page: {
    atCapacity: "Mythex is at capacity right now. Please try again in a moment.",
    openFailed: "We couldn't open this workspace. It may have been deleted, or the server is unreachable.",
    backHome: "Back home",
    tooManyTabs: "Close a tab first (max {max}).",
    githubConnected: "GitHub connected.",
    startingPublish: "Starting your workspace — the agent will publish when it’s ready.",
    renameChatFailed: "Couldn't rename the chat.",
    deleteChatFailed: "Couldn't delete the chat.",
    menu: "Menu",
    openMenu: "Open menu",
    openSidebar: "Open sidebar",
    projectMenu: "Project menu",
    goToDashboard: "Go to dashboard",
    knowledge: "Knowledge",
    secrets: "Secrets",
    export: "Export",
    hideChat: "Hide chat",
    showChat: "Show chat",
    toggleChat: "Toggle chat",
    workspaceView: "Workspace view",
    preview: "Preview",
    previewShort: "Prev",
    code: "Code",
    chat: "Chat",
    forward: "Forward",
    previewBack: "Preview back",
    previewForward: "Preview forward",
    switchToMobile: "Switch to Mobile",
    switchToDesktop: "Switch to Desktop",
    previewTarget: "Preview target",
    switchTarget: "Switch what you're previewing",
    main: "Main",
    appPath: "App path",
    previewWillAppear: "Preview will appear here",
    refreshPreview: "Refresh preview",
    selectElement: "Select an element",
    showPreview: "Show preview",
    publish: "Publish",
    publishing: "Publishing",
    publishingEllipsis: "Publishing…",
    refresh: "Refresh",
    viewApp: "View app",
    viewCode: "View code",
    copyPreviewLink: "Copy preview link",
    previewLinkCopied: "Preview link copied",
    openInCodeTitle: "Open {name} in Code?",
    openInCodeSubtitle: "The right pane switches from the preview to the code editor.",
  },
};
